import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { products } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import SEO from '../components/SEO';

const OrderKeiTruck = () => {
  const { language } = useLanguage();
  const { formatPrice } = useCurrency();
  const fr = language === 'fr';
  const product = products.find(p => p.id === 'kei-truck');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState('');
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    city: '',
    province: '',
    postalCode: '',
    country: 'Canada',
  });
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    company: '', 
    message: '' 
  });

  const ADD_ONS = [
    { id: 'windows', name: fr ? 'Fenêtres' : 'Windows', description: fr ? 'Fenêtres supplémentaires pour la lumière naturelle' : 'Additional windows for natural light' },
    { id: 'roof-hatch', name: fr ? 'Trappe de toit' : 'Roof Hatch', description: fr ? 'Ventilation et accès au toit' : 'Ventilation and roof access' },
    { id: 'entry-door', name: fr ? 'Porte d’entrée' : 'Entry Door', description: fr ? 'Installation de porte latérale' : 'Side entry door installation' },
  ];

  const BASE_PRICE = 5795.00;

  const toggleAddOn = (addOn) => {
    setSelectedAddOns(prev =>
      prev.find(a => a.id === addOn.id)
        ? prev.filter(a => a.id !== addOn.id)
        : [...prev, addOn]
    );
  };

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Step 1: Save to backend FIRST to get order ID
      let orderId = null;
      try {
        const dbRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'kei_truck_order',
            customer_name: formData.name,
            customer_email: formData.email,
            customer_phone: formData.phone,
            customer_company: formData.company,
            product: 'KEI TRUCK BOX',
            series: '',
            message: formData.message,
            source_page: '/order-kei-truck',
            details: {
              basePrice: formatPrice(BASE_PRICE),
              selectedAddOns: selectedAddOns.map(a => a.name).join(', ') || 'None',
              deliveryMethod: deliveryMethod === 'pickup' ? 'Pick up at Warehouse (Victoria, BC)' : 'Shipping',
              ...(deliveryMethod === 'shipping' && {
                shippingStreet: shippingAddress.street,
                shippingCity: shippingAddress.city,
                shippingProvince: shippingAddress.province,
                shippingPostalCode: shippingAddress.postalCode,
                shippingCountry: shippingAddress.country,
              }),
            },
          }),
        });
        const dbData = await dbRes.json();
        orderId = dbData.id;
      } catch (dbErr) {
        console.warn('Backend save failed:', dbErr);
      }

      // Step 2: Send to Web3Forms with admin dashboard link
      const adminLink = orderId
        ? `${window.location.origin}/portal/admin?tab=orders&order=${orderId}`
        : '';
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '4deb5ebc-3d5b-446d-9890-9acae8b33da4',
          subject: 'Order Inquiry - KEI TRUCK BOX',
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          message: formData.message,
          product: 'KEI TRUCK BOX',
          basePrice: `$${BASE_PRICE.toLocaleString('en-CA', { minimumFractionDigits: 2 })} CAD`,
          selectedAddOns: selectedAddOns.map(a => a.name).join(', ') || 'None',
          deliveryMethod: deliveryMethod === 'pickup' ? 'Pick up at Warehouse (Victoria, BC)' : 'Shipping',
          ...(deliveryMethod === 'shipping' && {
            shippingAddress: `${shippingAddress.street}, ${shippingAddress.city}, ${shippingAddress.province} ${shippingAddress.postalCode}, ${shippingAddress.country}`,
          }),
          'View in Admin Dashboard': adminLink || 'Link unavailable',
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setSelectedAddOns([]);
        setDeliveryMethod('');
        setShippingAddress({ street: '', city: '', province: '', postalCode: '', country: 'Canada' });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-36 flex flex-col items-center justify-center">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#FFF0E8' }}>
            <CheckCircle2 size={32} style={{ color: '#E8520A' }} />
          </div>
          <h2 className="text-2xl font-black text-gray-900 mb-3">
            {fr ? 'Commande reçue !' : 'Order Received!'}
          </h2>
          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            {fr
              ? 'Merci pour votre commande. Un représentant vous contactera sous peu pour confirmer les détails et organiser la livraison.'
              : 'Thank you for your order. A representative will contact you shortly to confirm details and arrange delivery.'}
          </p>
          <Link to="/products?tab=kei-truck" className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: '#E8520A' }}>
            {fr ? '← Retour aux produits' : '← Back to Products'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-[76px] bg-gray-50">
      <SEO
        title="Order Kei Truck Box"
        description="Order a Total Composites Kei Truck composite box. Lightweight, insulated cargo shell designed specifically for Japanese Kei trucks."
        path="/order-kei-truck"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <Link to="/products?tab=kei-truck" className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:opacity-80 transition-opacity" style={{ color: '#E8520A' }}>
          <ArrowLeft size={16} /> {fr ? 'Retour au Kei Truck Box' : 'Back to Kei Truck Box'}
        </Link>

        <div className="mb-10">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 mb-2 uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>
            {fr ? 'Commander le Kei Truck Box' : 'Order Kei Truck Box'}
          </h1>
          <p className="text-gray-500 text-sm">
            {fr
              ? 'Remplissez le formulaire ci-dessous pour commander votre caisson cargo composite ultra-léger isolé.'
              : 'Complete the form below to order your ultra-light insulated composite cargo box.'}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left — Product Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-6 border border-gray-200 sticky top-24">
              <img 
                src={product.image} 
                alt="Kei Truck Box" 
                className="w-full rounded-lg mb-4 bg-gray-50"
              />
              <h3 className="text-lg font-black text-gray-900 mb-2">KEI TRUCK BOX</h3>
              <p className="text-sm text-gray-500 mb-4">
                {fr ? 'Caisson cargo composite ultra-léger isolé' : 'Ultra-Light Insulated Composite Cargo Box'}
              </p>
              
              <div className="border-t border-gray-200 pt-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">{fr ? 'Prix de base' : 'Base Price'}</span>
                  <span className="text-lg font-black text-gray-900">{formatPrice(BASE_PRICE)}</span>
                </div>
              </div>

              {selectedAddOns.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-3">
                    {fr ? 'Options sélectionnées' : 'Selected Add-Ons'}
                  </p>
                  <ul className="space-y-2">
                    {selectedAddOns.map(addOn => (
                      <li key={addOn.id} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#E8520A' }} />
                        {addOn.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {deliveryMethod && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">
                    {fr ? 'Livraison' : 'Delivery'}
                  </p>
                  <p className="text-sm text-gray-700">
                    {deliveryMethod === 'pickup'
                      ? (fr ? 'Cueillette — Victoria, BC' : 'Pickup — Victoria, BC')
                      : (fr ? 'Expédition à l\'adresse' : 'Shipping to address')}
                  </p>
                  {deliveryMethod === 'shipping' && shippingAddress.city && (
                    <p className="text-xs text-gray-500 mt-1">
                      {shippingAddress.city}{shippingAddress.province ? `, ${shippingAddress.province}` : ''}{shippingAddress.country ? `, ${shippingAddress.country}` : ''}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right — Order Form */}
          <form onSubmit={handleSubmit} className="lg:col-span-3 bg-white rounded-2xl p-8 border border-gray-200">
            {/* Honeypot for spam protection - hidden from users */}
            <input type="checkbox" name="botcheck" style={{ display: 'none' }} tabIndex="-1" autoComplete="off" />
            
            {/* Add-Ons Selection */}
            <div className="mb-8">
              <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4">
                {fr ? 'Options supplémentaires' : 'Optional Add-Ons'}
              </h3>
              <div className="space-y-3">
                {ADD_ONS.map(addOn => (
                  <button
                    key={addOn.id}
                    type="button"
                    onClick={() => toggleAddOn(addOn)}
                    className={`w-full text-left border-2 rounded-xl p-4 transition-all duration-200 ${
                      selectedAddOns.find(a => a.id === addOn.id)
                        ? 'border-orange-400 bg-orange-50'
                        : 'border-gray-200 hover:border-orange-300'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                        selectedAddOns.find(a => a.id === addOn.id)
                          ? 'border-orange-500 bg-orange-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedAddOns.find(a => a.id === addOn.id) && (
                          <CheckCircle2 size={14} className="text-white" />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{addOn.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{addOn.description}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Delivery Method — Mandatory */}
            <div className="mb-8">
              <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4">
                {fr ? 'Méthode de livraison' : 'Delivery Method'} <span className="text-red-500">*</span>
              </h3>
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setDeliveryMethod('pickup')}
                  className={`w-full text-left border-2 rounded-xl p-4 transition-all duration-200 ${
                    deliveryMethod === 'pickup'
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                  data-testid="delivery-pickup"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                      deliveryMethod === 'pickup' ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                    }`}>
                      {deliveryMethod === 'pickup' && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {fr ? 'Cueillette à l\'entrepôt' : 'Pick up at Warehouse'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">Victoria, BC, Canada</p>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setDeliveryMethod('shipping')}
                  className={`w-full text-left border-2 rounded-xl p-4 transition-all duration-200 ${
                    deliveryMethod === 'shipping'
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300'
                  }`}
                  data-testid="delivery-shipping"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                      deliveryMethod === 'shipping' ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                    }`}>
                      {deliveryMethod === 'shipping' && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        {fr ? 'Expédition à l\'adresse' : 'Shipping to Address'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {fr ? 'Frais d\'expédition à déterminer' : 'Shipping costs to be determined'}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Shipping Address Fields */}
              {deliveryMethod === 'shipping' && (
                <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3" data-testid="shipping-address-fields">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{fr ? 'Adresse' : 'Street Address'} *</label>
                    <input
                      type="text"
                      required
                      value={shippingAddress.street}
                      onChange={(e) => setShippingAddress(p => ({ ...p, street: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                      data-testid="shipping-street"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{fr ? 'Ville' : 'City'} *</label>
                      <input
                        type="text"
                        required
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress(p => ({ ...p, city: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                        data-testid="shipping-city"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        {fr ? 'Province / État' : 'Province / State'}
                        {(shippingAddress.country === 'Canada' || shippingAddress.country === 'United States') && ' *'}
                      </label>
                      <input
                        type="text"
                        required={shippingAddress.country === 'Canada' || shippingAddress.country === 'United States'}
                        value={shippingAddress.province}
                        onChange={(e) => setShippingAddress(p => ({ ...p, province: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                        data-testid="shipping-province"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{fr ? 'Code postal' : 'Postal / Zip Code'} *</label>
                      <input
                        type="text"
                        required
                        value={shippingAddress.postalCode}
                        onChange={(e) => setShippingAddress(p => ({ ...p, postalCode: e.target.value }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                        data-testid="shipping-postal"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{fr ? 'Pays' : 'Country'} *</label>
                      <select
                        required
                        value={shippingAddress.country}
                        onChange={(e) => setShippingAddress(p => ({ ...p, country: e.target.value, province: '' }))}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                        data-testid="shipping-country"
                      >
                        <option value="Canada">Canada</option>
                        <option value="United States">United States</option>
                        <option disabled>──────────</option>
                        <option value="Afghanistan">Afghanistan</option>
                        <option value="Albania">Albania</option>
                        <option value="Algeria">Algeria</option>
                        <option value="Andorra">Andorra</option>
                        <option value="Angola">Angola</option>
                        <option value="Argentina">Argentina</option>
                        <option value="Armenia">Armenia</option>
                        <option value="Australia">Australia</option>
                        <option value="Austria">Austria</option>
                        <option value="Azerbaijan">Azerbaijan</option>
                        <option value="Bahamas">Bahamas</option>
                        <option value="Bahrain">Bahrain</option>
                        <option value="Bangladesh">Bangladesh</option>
                        <option value="Barbados">Barbados</option>
                        <option value="Belarus">Belarus</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Belize">Belize</option>
                        <option value="Benin">Benin</option>
                        <option value="Bhutan">Bhutan</option>
                        <option value="Bolivia">Bolivia</option>
                        <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                        <option value="Botswana">Botswana</option>
                        <option value="Brazil">Brazil</option>
                        <option value="Brunei">Brunei</option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Burkina Faso">Burkina Faso</option>
                        <option value="Burundi">Burundi</option>
                        <option value="Cambodia">Cambodia</option>
                        <option value="Cameroon">Cameroon</option>
                        <option value="Cape Verde">Cape Verde</option>
                        <option value="Central African Republic">Central African Republic</option>
                        <option value="Chad">Chad</option>
                        <option value="Chile">Chile</option>
                        <option value="China">China</option>
                        <option value="Colombia">Colombia</option>
                        <option value="Comoros">Comoros</option>
                        <option value="Congo">Congo</option>
                        <option value="Costa Rica">Costa Rica</option>
                        <option value="Croatia">Croatia</option>
                        <option value="Cuba">Cuba</option>
                        <option value="Cyprus">Cyprus</option>
                        <option value="Czech Republic">Czech Republic</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Djibouti">Djibouti</option>
                        <option value="Dominican Republic">Dominican Republic</option>
                        <option value="Ecuador">Ecuador</option>
                        <option value="Egypt">Egypt</option>
                        <option value="El Salvador">El Salvador</option>
                        <option value="Equatorial Guinea">Equatorial Guinea</option>
                        <option value="Eritrea">Eritrea</option>
                        <option value="Estonia">Estonia</option>
                        <option value="Eswatini">Eswatini</option>
                        <option value="Ethiopia">Ethiopia</option>
                        <option value="Fiji">Fiji</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="Gabon">Gabon</option>
                        <option value="Gambia">Gambia</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Germany">Germany</option>
                        <option value="Ghana">Ghana</option>
                        <option value="Greece">Greece</option>
                        <option value="Grenada">Grenada</option>
                        <option value="Guatemala">Guatemala</option>
                        <option value="Guinea">Guinea</option>
                        <option value="Guyana">Guyana</option>
                        <option value="Haiti">Haiti</option>
                        <option value="Honduras">Honduras</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Iceland">Iceland</option>
                        <option value="India">India</option>
                        <option value="Indonesia">Indonesia</option>
                        <option value="Iran">Iran</option>
                        <option value="Iraq">Iraq</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Israel">Israel</option>
                        <option value="Italy">Italy</option>
                        <option value="Jamaica">Jamaica</option>
                        <option value="Japan">Japan</option>
                        <option value="Jordan">Jordan</option>
                        <option value="Kazakhstan">Kazakhstan</option>
                        <option value="Kenya">Kenya</option>
                        <option value="Kuwait">Kuwait</option>
                        <option value="Kyrgyzstan">Kyrgyzstan</option>
                        <option value="Laos">Laos</option>
                        <option value="Latvia">Latvia</option>
                        <option value="Lebanon">Lebanon</option>
                        <option value="Lesotho">Lesotho</option>
                        <option value="Liberia">Liberia</option>
                        <option value="Libya">Libya</option>
                        <option value="Liechtenstein">Liechtenstein</option>
                        <option value="Lithuania">Lithuania</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Madagascar">Madagascar</option>
                        <option value="Malawi">Malawi</option>
                        <option value="Malaysia">Malaysia</option>
                        <option value="Maldives">Maldives</option>
                        <option value="Mali">Mali</option>
                        <option value="Malta">Malta</option>
                        <option value="Mauritania">Mauritania</option>
                        <option value="Mauritius">Mauritius</option>
                        <option value="Mexico">Mexico</option>
                        <option value="Moldova">Moldova</option>
                        <option value="Monaco">Monaco</option>
                        <option value="Mongolia">Mongolia</option>
                        <option value="Montenegro">Montenegro</option>
                        <option value="Morocco">Morocco</option>
                        <option value="Mozambique">Mozambique</option>
                        <option value="Myanmar">Myanmar</option>
                        <option value="Namibia">Namibia</option>
                        <option value="Nepal">Nepal</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="New Zealand">New Zealand</option>
                        <option value="Nicaragua">Nicaragua</option>
                        <option value="Niger">Niger</option>
                        <option value="Nigeria">Nigeria</option>
                        <option value="North Macedonia">North Macedonia</option>
                        <option value="Norway">Norway</option>
                        <option value="Oman">Oman</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Panama">Panama</option>
                        <option value="Papua New Guinea">Papua New Guinea</option>
                        <option value="Paraguay">Paraguay</option>
                        <option value="Peru">Peru</option>
                        <option value="Philippines">Philippines</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Qatar">Qatar</option>
                        <option value="Romania">Romania</option>
                        <option value="Russia">Russia</option>
                        <option value="Rwanda">Rwanda</option>
                        <option value="Saudi Arabia">Saudi Arabia</option>
                        <option value="Senegal">Senegal</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Singapore">Singapore</option>
                        <option value="Slovakia">Slovakia</option>
                        <option value="Slovenia">Slovenia</option>
                        <option value="Somalia">Somalia</option>
                        <option value="South Africa">South Africa</option>
                        <option value="South Korea">South Korea</option>
                        <option value="Spain">Spain</option>
                        <option value="Sri Lanka">Sri Lanka</option>
                        <option value="Sudan">Sudan</option>
                        <option value="Suriname">Suriname</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Syria">Syria</option>
                        <option value="Taiwan">Taiwan</option>
                        <option value="Tajikistan">Tajikistan</option>
                        <option value="Tanzania">Tanzania</option>
                        <option value="Thailand">Thailand</option>
                        <option value="Togo">Togo</option>
                        <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                        <option value="Tunisia">Tunisia</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Turkmenistan">Turkmenistan</option>
                        <option value="Uganda">Uganda</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Uruguay">Uruguay</option>
                        <option value="Uzbekistan">Uzbekistan</option>
                        <option value="Venezuela">Venezuela</option>
                        <option value="Vietnam">Vietnam</option>
                        <option value="Yemen">Yemen</option>
                        <option value="Zambia">Zambia</option>
                        <option value="Zimbabwe">Zimbabwe</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Information */}
            <div className="mb-8">
              <h3 className="text-sm font-bold uppercase tracking-widest text-gray-400 mb-4">
                {fr ? 'Vos informations' : 'Your Information'}
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{fr ? 'Nom' : 'Name'} *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{fr ? 'Courriel' : 'Email'} *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{fr ? 'Téléphone' : 'Phone'} *</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{fr ? 'Entreprise' : 'Company'}</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                {fr ? 'Informations supplémentaires' : 'Additional Information'}
              </label>
              <textarea
                name="message"
                rows={4}
                value={formData.message}
                onChange={handleChange}
                placeholder={fr ? 'Exigences spéciales ou questions...' : 'Any special requirements or questions...'}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all text-sm text-gray-900"
              />
            </div>

            {/* Agreement */}
            <label className="flex items-start gap-3 mb-6 cursor-pointer group">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 border-2 border-gray-300 rounded cursor-pointer"
                style={{ accentColor: '#E8520A' }}
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                {fr
                  ? "Je confirme que toutes les informations fournies sont exactes et je comprends qu’un représentant me contactera pour confirmer les détails de la commande et le calendrier de livraison."
                  : 'I confirm that all information provided is accurate and I understand that a representative will contact me to confirm order details and delivery timeline.'}
              </span>
            </label>

            {/* Submit */}
            <button
              type="submit"
              disabled={!agreed || !deliveryMethod || submitting}
              className={`w-full px-7 py-3.5 font-bold text-white rounded-full text-sm transition-all duration-200 ${
                !agreed || !deliveryMethod || submitting ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90 hover:scale-[1.02]'
              }`}
              style={{ backgroundColor: '#E8520A' }}
              data-testid="submit-kei-order"
            >
              {submitting
                ? (fr ? 'Envoi en cours...' : 'Submitting...')
                : (fr ? 'SOUMETTRE LA COMMANDE' : 'SUBMIT ORDER')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrderKeiTruck;
